import { DataConnectorsType } from '@/types/DataConnectorsType';

export interface WelcomePrompt {
  text: string;
  icon?: 'spark' | 'alert' | 'fleet' | 'sync' | 'build';
}

export interface WelcomePromptSet {
  primary: WelcomePrompt[];
  more: WelcomePrompt[];
}

type PageContextType = string;

const TELEMATICS_HINTS = [
  'samsara',
  'geotab',
  'motive',
  'keeptruckin',
  'eroad',
  'lytx',
  'detroit',
  'paccar',
  'volvo',
  'cummins',
  'ford pro',
  'mack',
  'international',
  'motorq',
  'onstar',
  'mercedes',
  'rfms',
  'telematics',
  'eld',
  'hos',
  'omnitracs',
  'peoplenet',
];

function blobText(item: DataConnectorsType): string {
  const connector = item.connector as Record<string, any> | undefined;
  const integration = item.integration as Record<string, any> | undefined;
  const profile = connector?.integrationProfile || integration?.integrationProfile;
  return [
    connectorName(item),
    connector?.connectorType,
    connector?.details,
    profile?.integrationName,
    profile?.type,
    item.source?.name,
    item.destination?.name,
    item.config?.source?.name,
    item.config?.destination?.name,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

export function connectorName(item: DataConnectorsType): string {
  const connector = item.connector as Record<string, any> | undefined;
  const integration = item.integration as Record<string, any> | undefined;
  return (
    connector?.integrationProfile?.integrationName ||
    connector?.integrationName ||
    connector?.name ||
    integration?.integrationName ||
    integration?.name ||
    item.integrationName ||
    item.name ||
    item.config?.name ||
    'this integration'
  );
}

function normalizeStatus(item: DataConnectorsType): string {
  return String(item.connector?.status || item.integration?.status || item.config?.status || '')
    .toLowerCase();
}

function isTelematicsWorkspace(connectors: DataConnectorsType[]): boolean {
  return connectors.some((item) =>
    TELEMATICS_HINTS.some((hint) => blobText(item).includes(hint.toLowerCase()))
  );
}

function pagePrompts(pageType: PageContextType, integrationName?: string): WelcomePromptSet | null {
  const name = integrationName || 'this integration';

  switch (pageType) {
    case 'integration-studio':
      return {
        primary: [
          { text: `Help me finish configuring ${name}`, icon: 'build' },
          { text: 'How do I set up authentication here?', icon: 'spark' },
          { text: 'Test my current connection', icon: 'sync' },
        ],
        more: [
          { text: "What's wrong with my settings?" },
          { text: 'What polling interval should I use?' },
        ],
      };
    case 'data-mapping':
      return {
        primary: [
          { text: `Suggest field mappings for ${name}`, icon: 'spark' },
          { text: 'How do I transform this field?', icon: 'build' },
          { text: 'Validate my current mapping', icon: 'sync' },
        ],
        more: [
          { text: 'Add a calculated field here' },
          { text: 'How do I map nested fields?' },
        ],
      };
    case 'integration-details':
      return {
        primary: [
          { text: `Analyze the performance data for ${name}`, icon: 'spark' },
          { text: 'Are there any issues I should address?', icon: 'alert' },
          { text: 'How can I optimize this integration?', icon: 'build' },
        ],
        more: [
          { text: 'What do these metrics tell me?' },
          { text: 'Show me failed transactions' },
        ],
      };
    case 'integration-builder':
      return {
        primary: [
          { text: 'Help me design this integration', icon: 'build' },
          { text: 'What connectors should I use?', icon: 'spark' },
          { text: 'Set up bi-directional sync', icon: 'sync' },
        ],
        more: [
          { text: 'Add error handling' },
          { text: 'How do I connect REST APIs?' },
        ],
      };
    default:
      return null;
  }
}

export function getMaestroWelcomePrompts(
  connectors: DataConnectorsType[],
  pageType: PageContextType = 'dashboard',
  integrationName?: string
): WelcomePromptSet {
  const pageSpecific = pagePrompts(pageType, integrationName);
  if (pageSpecific) return pageSpecific;

  const errored = connectors.filter((item) => {
    const status = normalizeStatus(item);
    const sync = String(item.syncStatus || '').toLowerCase();
    return status === 'error' || sync === 'failed';
  });
  const paused = connectors.filter((item) => normalizeStatus(item) === 'pause');
  const stale = connectors.filter((item) => String(item.syncStatus || '').toLowerCase() === 'stale');
  const drafts = connectors.filter((item) =>
    ['not completed', 'draft', 'not_completed'].includes(normalizeStatus(item))
  );
  const hasTelematics = isTelematicsWorkspace(connectors);
  const primary: WelcomePrompt[] = [];
  const more: WelcomePrompt[] = [];

  if (errored.length) {
    const name = connectorName(errored[0]);
    primary.push({
      text:
        errored.length === 1
          ? `Why is ${name} failing?`
          : `Which integrations need attention right now?`,
      icon: 'alert',
    });
  }

  if (stale.length) {
    primary.push({
      text: 'Which connectors have not synced recently?',
      icon: 'sync',
    });
  }

  if (paused.length && primary.length < 3) {
    primary.push({
      text: 'Which integrations are paused and should be resumed?',
      icon: 'alert',
    });
  }

  if (drafts.length && primary.length < 3) {
    primary.push({
      text: `Finish setting up ${connectorName(drafts[0])}`,
      icon: 'build',
    });
  }

  if (!connectors.length) {
    primary.push(
      { text: 'Create a new integration from a connector', icon: 'build' },
      { text: 'How do I connect my TMS to telematics?', icon: 'fleet' },
      { text: 'What can Maestro do with my operations data?', icon: 'spark' }
    );
    more.push(
      { text: 'Walk me through connecting a REST API' },
      { text: 'How do I monitor integration health?' }
    );
    return { primary: primary.slice(0, 3), more };
  }

  if (hasTelematics) {
    if (primary.length < 3) {
      primary.push({
        text: 'Which vehicles have not sent a location update recently?',
        icon: 'fleet',
      });
    }
    more.push(
      { text: 'Which drivers are closest to their HOS limits right now?' },
      { text: 'What is on-time performance looking like this week?' }
    );
  }

  if (primary.length < 3) {
    primary.push({
      text: 'Which integrations have the highest error rate this week?',
      icon: 'alert',
    });
  }
  if (primary.length < 3) {
    primary.push({
      text: 'Summarize integration health across my workspace',
      icon: 'spark',
    });
  }
  if (primary.length < 3) {
    primary.push({
      text: 'What should I look at first this morning?',
      icon: 'spark',
    });
  }

  more.push(
    { text: 'Create a new integration' },
    { text: 'Which integrations need attention?' },
    { text: 'Show me system health' }
  );

  const seen = new Set<string>();
  const dedupe = (list: WelcomePrompt[]) =>
    list.filter((item) => {
      if (seen.has(item.text)) return false;
      seen.add(item.text);
      return true;
    });

  return {
    primary: dedupe(primary).slice(0, 3),
    more: dedupe(more).slice(0, 4),
  };
}

export function greetingForNow(firstName?: string, email?: string): { hello: string; prompt: string } {
  const hour = new Date().getHours();
  const timeOfDay = hour < 12 ? 'morning' : hour < 18 ? 'afternoon' : 'evening';
  const name =
    firstName?.trim() ||
    email?.split('@')[0] ||
    '';
  const hello = name ? `Good ${timeOfDay}, ${name}.` : `Good ${timeOfDay}.`;
  return {
    hello,
    prompt: 'What are we tackling today?',
  };
}
